/**
 * Achievements wall (presentational) — the third view of the Stats tab. Draws the
 * three basic score achievements from the pure {@link ACHIEVEMENTS} catalog,
 * marking which the player has unlocked and — the network angle the owner asked
 * for — HOW MANY players have each one (from the public
 * {@link import('../services').NetworkStats} `achievementCounts`).
 *
 * Presentational only: the shell hands in the player's unlocked ids + the network
 * counts; this component computes nothing and talks to no service.
 */
import { ACHIEVEMENTS } from '../core/achievements';
import { useT } from './i18n';

interface AchievementsViewProps {
  /** The achievement ids the current player has unlocked (for this game). */
  unlocked: readonly string[];
  /** achievementId → number of players who have unlocked it (network-wide). */
  counts: Record<string, number>;
  /** Total players tracked for this game (denominator for the share bar). */
  playersTracked: number;
  loading?: boolean;
}

export function AchievementsView({ unlocked, counts, playersTracked, loading }: AchievementsViewProps) {
  const t = useT();
  const have = new Set(unlocked);
  const earnedCount = ACHIEVEMENTS.filter((a) => have.has(a.id)).length;

  return (
    <section className="panel achievements">
      <style>{ACHIEVEMENTS_CSS}</style>
      <div className="ach-head">
        <h2>{t('ach.heading')}</h2>
        <span className="ach-progress">
          {earnedCount} <span className="ach-progress-of">/ {ACHIEVEMENTS.length}</span> {t('ach.earned')}
        </span>
      </div>

      {loading ? (
        <p className="ach-status">{t('ach.loading')}</p>
      ) : (
        <ul className="ach-list">
          {ACHIEVEMENTS.map((a) => {
            const owned = have.has(a.id);
            const playerCount = counts[a.id] ?? 0;
            const pct =
              playersTracked > 0 ? Math.min(100, Math.round((playerCount / playersTracked) * 100)) : 0;
            return (
              <li key={a.id} className={'ach-row' + (owned ? ' is-owned' : '')}>
                <span className="ach-icon" aria-hidden>
                  {a.icon}
                </span>
                <div className="ach-body">
                  <div className="ach-title-row">
                    <span className="ach-title">{t('ach.' + a.id + '.name', a.name)}</span>
                    {owned ? (
                      <span className="ach-pill ach-pill-owned">{t('ach.unlocked')}</span>
                    ) : (
                      <span className="ach-pill">{t('ach.locked')}</span>
                    )}
                  </div>
                  <span className="ach-desc">{t('ach.' + a.id + '.desc', a.desc)}</span>
                  <div className="ach-network" title={`${playerCount.toLocaleString()} players`}>
                    <span className="ach-network-bar" aria-hidden>
                      <span className="ach-network-fill" style={{ width: `${pct}%` }} />
                    </span>
                    <span className="ach-network-count">
                      {playerCount.toLocaleString()} {playerCount === 1 ? t('ach.has1') : t('ach.hasN')}
                      {playersTracked > 0 && <span className="ach-network-pct"> · {pct}%</span>}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const ACHIEVEMENTS_CSS = `
.achievements { width: min(440px, 92vw); }
.achievements .ach-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.achievements .ach-progress {
  font-size: 13px;
  font-weight: 800;
  color: var(--gold);
  font-variant-numeric: tabular-nums;
}
.achievements .ach-progress-of { color: var(--muted); font-weight: 600; }
.achievements .ach-status { color: var(--muted); margin: 8px 0 0; }
.achievements .ach-list {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.achievements .ach-row {
  display: grid;
  grid-template-columns: 46px 1fr;
  gap: 12px;
  align-items: center;
  padding: 14px;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius);
  opacity: 0.72;
  transition: opacity var(--t) var(--ease), border-color var(--t) var(--ease),
    box-shadow var(--t) var(--ease);
}
.achievements .ach-row.is-owned {
  opacity: 1;
  border-color: rgba(255, 209, 102, 0.45);
  box-shadow: var(--shadow-1), 0 0 20px rgba(255, 209, 102, 0.14);
}
.achievements .ach-icon {
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  font-size: 24px;
  background: var(--panel);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-sm);
}
.achievements .ach-row.is-owned .ach-icon {
  background: linear-gradient(135deg, var(--accent-tint), var(--panel));
  border-color: var(--glass-border-strong);
}
.achievements .ach-body { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.achievements .ach-title-row { display: flex; align-items: center; gap: 8px; justify-content: space-between; }
.achievements .ach-title { font-weight: 800; font-size: 15px; color: var(--text); }
.achievements .ach-pill {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted);
  background: var(--panel);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  padding: 3px 9px;
  white-space: nowrap;
}
.achievements .ach-pill-owned {
  color: var(--accent-ink);
  background: linear-gradient(135deg, var(--accent), var(--gold));
  border-color: transparent;
}
.achievements .ach-desc { font-size: 13px; color: var(--muted); line-height: 1.4; }
.achievements .ach-network { display: flex; align-items: center; gap: 8px; margin-top: 2px; }
.achievements .ach-network-bar {
  flex: 1;
  height: 5px;
  border-radius: var(--radius-pill);
  background: var(--panel);
  overflow: hidden;
}
.achievements .ach-network-fill {
  display: block;
  height: 100%;
  border-radius: var(--radius-pill);
  background: linear-gradient(90deg, var(--accent), var(--gold));
  transition: width var(--t-slow) var(--ease);
}
.achievements .ach-network-count {
  font-size: 11px;
  color: var(--text-dim);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.achievements .ach-network-pct { color: var(--muted); }
`;
